import { createTheme } from "@material-ui/core/styles";
import { fontSizeScale, fontWeight } from "./constants/typography";

/**
 * MUI v4 Theme
 * ─────────────
 * All font-size values are sourced from constants/typography.js.
 * To change a size application-wide, edit that file — not here.
 *
 * Variant → role mapping:
 *   h5       → page / screen titles
 *   h6       → section headings  (e.g. "Alternative Sample Information")
 *   subtitle1 → sub-section headings
 *   body1    → default body text
 *   body2    → data labels / field labels
 *   caption  → helper / small text
 *   button   → button labels
 */
export const theme = createTheme({
    typography: {
        fontFamily: `"Nunito", "Helvetica", "Arial", sans-serif`,

        // page / screen title
        h5: {
            fontSize: fontSizeScale.pageTitle,       // 16px
            fontWeight: fontWeight.semiBold,
            lineHeight: 1.3,
        },
        // section heading (e.g. "Alternative Sample Information") — 13px per UI spec
        h6: {
            fontSize: fontSizeScale.subHeading,      // 13px
            fontWeight: fontWeight.semiBold,
            lineHeight: 1.4,
        },
        // sub-section heading (inside accordions)
        subtitle1: {
            fontSize: fontSizeScale.subHeading,      // 13px
            fontWeight: fontWeight.medium,
            lineHeight: 1.4,
        },
        subtitle2: {
            fontSize: fontSizeScale.body,            // 13px
            fontWeight: fontWeight.medium,
            lineHeight: 1.4,
        },
        // default body text
        body1: {
            fontSize: fontSizeScale.body,            // 13px
            fontWeight: fontWeight.regular,
            lineHeight: 1.5,
        },
        // data labels / field labels
        body2: {
            fontSize: fontSizeScale.dataLabel,       // 12px
            fontWeight: fontWeight.regular,
            lineHeight: 1.4,
        },
        // helper / small text
        caption: {
            fontSize: fontSizeScale.caption,         // 11px
            fontWeight: fontWeight.regular,
            lineHeight: 1.3,
        },
        // button labels
        button: {
            fontSize: fontSizeScale.button,          // 11px
            fontWeight: fontWeight.medium,
            textTransform: "none",
            lineHeight: 1.4,
        },
        // overline (rarely used - keep compact)
        overline: {
            fontSize: fontSizeScale.caption,         // 11px
            fontWeight: fontWeight.medium,
            lineHeight: 1.2,
            letterSpacing: "0.05em",
        },
    },

    overrides: {
        // Tabs
        MuiTab: {
            root: {
                fontSize: fontSizeScale.tabLabel,    // 14px
                fontWeight: fontWeight.medium,
                minHeight: 40,
                padding: "6px 14px",
                textTransform: "capitalize",
            },
        },
        MuiTabs: {
            root: { minHeight: 40 },
        },

        // Breadcrumbs
        MuiBreadcrumbs: {
            root: {
                fontSize: fontSizeScale.breadcrumb,  // 15px
            },
        },

        // Dialog titles
        MuiDialogTitle: {
            root: {
                "& h2": { fontSize: fontSizeScale.dialogTitle }, // 14px
            },
        },

        // Input labels
        MuiInputLabel: {
            root: {
                fontSize: fontSizeScale.dataLabel,   // 12px
            },
        },
        MuiFormLabel: {
            root: {
                fontSize: fontSizeScale.dataLabel,   // 12px
                textTransform: "none",
            },
        },
        MuiFormControlLabel: {
            label: {
                fontSize: fontSizeScale.dataLabel,   // 12px
            },
        },

        // Table cells
        MuiTableCell: {
            root: {
                fontSize: fontSizeScale.dataLabel,   // 12px
                padding: "6px 12px",
            },
            head: {
                fontSize: fontSizeScale.dataLabel,   // 12px
                fontWeight: fontWeight.bold,          // 700 — bold table headers
                letterSpacing: "0.01em",
                // Left-align all table headers app-wide, regardless of the
                // column's own `align` prop (e.g. numeric columns), via a
                // single shared theme rule instead of per-page overrides.
                textAlign: "left !important",
            },
        },

        // Input overrides — 44px height, 12px value text
        MuiOutlinedInput: {
            input: {
                padding: "11px 14px",
                fontSize: fontSizeScale.dataLabel,   // 12px
            },
        },
        MuiInputBase: {
            input: {
                fontSize: fontSizeScale.dataLabel,   // 12px
            },
        },
        MuiInputLabel: {
            root: {
                fontSize: fontSizeScale.dataLabel,   // 12px
            },
        },

        // Accordion section headers — 13px
        MuiAccordionSummary: {
            root: { minHeight: 40 },
            content: { margin: "8px 0" },
        },
    },
});
